/**
 * An extracted criterion's points, opened beneath its row — Story 20.8 — and,
 * since Story 20.9, the ICP fit's.
 *
 * Visual Reference: `design/screens/editor-qualify.html`, the `points` capture —
 * Experience's ranges open beneath the row inside the Criteria card, and the
 * One of, Yes/no, Text and Ideal customer match specimens beside it. Built from
 * their markup: a `--mdx-ground` panel, 16px in, 10px between rows; a 15px
 * reading and a 56px score; the range bounds at `--mdx-meta-column-xs`
 * (DECISION-152); and a last row, Nothing read or Not measured, under a
 * `--mdx-line` rule.
 *
 * The rows come from the TYPE the criterion reads — a field's, or the fit's
 * Number from 0 to 100 — and each shows what the stored map awards
 * (`extracted-points.ts`), so this renders a view and writes through `edit()` —
 * it decides nothing about what a map means.
 */
import { Fragment } from 'react'
import { COMPOSED, DRAWN } from './extracted-points-copy'
import {
  addRange,
  pointsView,
  setPoints,
  setRangeBound,
  type RangeRow,
  type Reads,
  type Scored,
  type Target,
} from './extracted-points'
import { edit } from './store'

/** The last row's words: "Nothing read" for a field, "Not measured" for the ICP fit (Story 20.9). */
export type NothingWords = { label: string; name: string }

export const NOTHING_READ: NothingWords = { label: DRAWN.nothingRead, name: DRAWN.scoreWhenNothing }

export function PointsPanel({
  id,
  criterion,
  field,
  nothing = NOTHING_READ,
}: {
  /** The panel's id, which the row's disclosure names in `aria-controls`. */
  id: string
  criterion: Scored
  field: Reads
  nothing?: NothingWords
}) {
  const view = pointsView(field, criterion.points)
  const score = (target: Target) => (points: number) =>
    edit('Score an extracted reading', (draft) => setPoints(draft, criterion.id, target, points), {
      coalesce: true,
    })

  return (
    <div id={id} className="mt-mdx-4 flex flex-col gap-mdx-10 rounded-mdx-control bg-mdx-ground p-mdx-16">
      <span className="text-mdx-14 font-mdx-semibold">{DRAWN.scoreOutOf(criterion.weight)}</span>

      {view.shape === 'ranges' ? (
        <>
          {view.ranges.map((range, index) => (
            // Ranges are added at the end and never reordered, so a range IS its
            // position — the same position the scorer checks them in.
            <Fragment key={index}>
              <RangeLine criterionId={criterion.id} index={index} range={range} onScore={score({ row: 'range', index })} />
              {range.backwards ? (
                <span className="text-mdx-13 text-mdx-review-ink">{COMPOSED.backwards}</span>
              ) : null}
            </Fragment>
          ))}
          <span className="max-w-mdx-measure-56 text-mdx-13 text-mdx-slate">{DRAWN.ranges}</span>
          <button
            type="button"
            onClick={() => edit(DRAWN.addRange, (draft) => addRange(draft, criterion.id))}
            // The Build tab's "Add an option", which the export draws the same.
            className="cursor-pointer self-start rounded-mdx-control-sm border-0 bg-mdx-brand-tint px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-brand-deep"
          >
            {DRAWN.addRange}
          </button>
          <NothingLine words={nothing} points={view.nothing} onScore={score({ row: 'nothing' })} />
        </>
      ) : view.shape === 'mention' ? (
        <>
          <Reading label={DRAWN.mentioned} name={DRAWN.scoreFor('mentioned')} points={view.present} onScore={score({ row: 'present' })} />
          <Reading
            label={DRAWN.notMentioned}
            name={DRAWN.scoreFor('not mentioned')}
            points={view.absent}
            onScore={score({ row: 'absent' })}
          />
        </>
      ) : (
        <>
          {view.rows.map((row) => (
            <Reading
              key={row.key}
              label={row.label}
              name={row.name}
              points={row.points}
              onScore={score({ row: 'value', key: row.key })}
            />
          ))}
          <NothingLine words={nothing} points={view.nothing} onScore={score({ row: 'nothing' })} />
        </>
      )}
    </div>
  )
}

/** One reading and what it is worth: a One of's value, Yes, No, Mentioned. */
function Reading({
  label,
  name,
  points,
  onScore,
}: {
  label: string
  name: string
  points: number
  onScore: (points: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-mdx-10 gap-y-mdx-8">
      <span className="min-w-0 flex-auto text-mdx-15">{label}</span>
      <ScoreInput name={name} points={points} onScore={onScore} />
    </div>
  )
}

/** The last row — the model read nothing — under a rule, in the quieter ink. */
function NothingLine({
  words,
  points,
  onScore,
}: {
  words: NothingWords
  points: number
  onScore: (points: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-mdx-10 gap-y-mdx-8 border-t border-mdx-line pt-mdx-10">
      <span className="min-w-0 flex-auto text-mdx-15 text-mdx-slate">{words.label}</span>
      <ScoreInput name={words.name} points={points} onScore={onScore} />
    </div>
  )
}

/**
 * `From [0] to [2]` and the range's score, pushed to the row's end. At 375 the
 * row wraps after `to` exactly as the capture does: the second bound and the
 * score take the next line.
 */
function RangeLine({
  criterionId,
  index,
  range,
  onScore,
}: {
  criterionId: string
  index: number
  range: RangeRow
  onScore: (points: number) => void
}) {
  const bound = (end: 'min' | 'max') => (value: number | null) =>
    edit('Change a range', (draft) => setRangeBound(draft, criterionId, index, end, value), { coalesce: true })

  return (
    <div className="flex flex-wrap items-center gap-x-mdx-10 gap-y-mdx-8">
      <span className="flex-none text-mdx-13 text-mdx-slate">{DRAWN.from}</span>
      <BoundInput name={range.fromName} value={range.min} onChange={bound('min')} />
      <span className="flex-none text-mdx-13 text-mdx-slate">{DRAWN.to}</span>
      <BoundInput name={range.toName} value={range.max} onChange={bound('max')} />
      <ScoreInput name={range.scoreName} points={range.points} onScore={onScore} className="ml-auto" />
    </div>
  )
}

/**
 * A range's end. Blank is no limit, which is `null` in the map and "No limit"
 * on the screen — at `--mdx-meta-column`, the width the capture gives the one
 * bound it leaves open, because the placeholder does not fit in 76px. A bound
 * with a number is `--mdx-meta-column-xs`, as drawn.
 */
function BoundInput({
  name,
  value,
  onChange,
}: {
  name: string
  value: number | null
  onChange: (value: number | null) => void
}) {
  return (
    <input
      type="number"
      aria-label={name}
      placeholder={DRAWN.noLimit}
      value={value === null ? '' : value}
      onChange={(event) => {
        const raw = event.target.value.trim()
        if (raw === '') {
          onChange(null)
          return
        }
        const next = Number(raw)
        if (Number.isFinite(next)) onChange(next)
      }}
      className={[
        'h-mdx-input flex-none rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-10',
        'text-right text-mdx-15 tabular-nums text-mdx-ink outline-none focus:border-mdx-brand',
        value === null ? 'w-mdx-meta-column' : 'w-mdx-meta-column-xs',
      ].join(' ')}
    />
  )
}

/**
 * What a reading is worth: a whole number from zero, 56px, as the capture draws
 * every score. Blank is zero, as the Build tab's option scores read it.
 */
function ScoreInput({
  name,
  points,
  onScore,
  className = '',
}: {
  name: string
  points: number
  onScore: (points: number) => void
  className?: string
}) {
  return (
    <input
      type="number"
      aria-label={name}
      min={0}
      step={1}
      value={points}
      onChange={(event) => {
        const raw = event.target.value.trim()
        const next = raw === '' ? 0 : Number(raw)
        if (Number.isInteger(next) && next >= 0) onScore(next)
      }}
      className={[
        'h-mdx-input w-mdx-56 flex-none rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-10',
        'text-right text-mdx-15 font-mdx-semibold tabular-nums text-mdx-ink outline-none focus:border-mdx-brand',
        className,
      ].join(' ')}
    />
  )
}
